import { ReactNode, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronButton,
  Heading,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import bgStart from '../../../assets/images/bg_start.svg';
import bgEnd from '../../../assets/images/bgEnd.png';
import { DTVendable } from '@rabbit/data/types';
import { AppContext } from '@rabbit/app-context';
import { subscribeDocument } from '@rabbit/utils/api';
import { useTenantBrandIdentity } from '../../../utils/hooks/useTenantBrandIdentity';

export interface AuthContainerProps {
  children: ReactNode;
  goBack?: null | (() => any);
  title?: string;
  background?: boolean;
  extraClasses?: string;
  currentStep?: number | null;
  loading?: boolean;
}

export function AuthContainer({
  children,
  goBack,
  title,
  background = false,
  extraClasses = '',
  currentStep,
  loading,
}: AuthContainerProps) {
  const [vendableLoading, setVendableLoading] = useState(false);
  const [vendable, setVendable] = useState<DTVendable>();
  const { tenantInfo } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const brandFromQuery = searchParams.get('brand');

  // Get brand identity if brand query param exists in brandDetails
  const { tenantBrandLogo, tenantBrandName, tenantBrandPlaceholder } =
    useTenantBrandIdentity({
      tenantInfo,
      brand: brandFromQuery || undefined,
    });

  // Use brand values if available, otherwise fallback to default tenant values
  const displayLogo = tenantBrandLogo || tenantInfo?.logo;
  const displayName = tenantBrandName || tenantInfo?.name;
  const displayPlaceholder =
    tenantBrandPlaceholder || tenantInfo?.productPlaceholder;

  useEffect(() => {
    let unSub = () => {};
    if (localStorage.vendableId) {
      setVendableLoading(true);
      unSub = subscribeDocument<DTVendable>(
        `vendable/${localStorage.vendableId}`,
        (data) => {
          setVendable(data.data());
          setVendableLoading(false);
          localStorage.removeItem('vendableId');
        }
      );
    }

    return () => {
      unSub();
    };
  }, []);

  const BackAndTitle = (
    <div className="relative mb-6 flex w-full flex-row">
      {goBack && (
        <ChevronButton
          className="absolute flex h-full cursor-pointer flex-wrap content-center"
          kind="back"
          onClick={goBack}
        />
      )}

      {title && (
        <div className="mx-auto text-center">
          <Heading kind="h2">{title}</Heading>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-canvas flex grow flex-col lg:content-center">
      <div
        style={{
          backgroundImage:
            !currentStep || currentStep === 1
              ? `${background ? 'url(' + bgStart + ')' : 'none'}`
              : '',
        }}
        className={`relative mt-5 w-full ${extraClasses} ${
          currentStep === 1
            ? 'md:m-auto md:grid md:w-[1008px] md:grid-cols-2 md:gap-12'
            : 'md:m-auto md:w-[480px]'
        }`}
      >
        {currentStep === 1 && (
          <div
            className={`relative rounded-[6px] bg-white px-8 py-12 md:px-16 md:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)]`}
          >
            {vendableLoading && <LoadingSpinner size="sm" overlay={true} />}
            <div className="flex flex-col">
              <img
                src={displayLogo}
                className="max-h-[72px] w-full object-contain"
              />
              <div className={`flex flex-col ${vendable ? 'gap-10' : ''}`}>
                <div>
                  <div
                    className={`flex items-center ${
                      vendable ? 'h-[360px]' : 'h-[400px]'
                    } w-full`}
                  >
                    <img
                      src={
                        vendable
                          ? vendable.img && vendable.img.length > 0
                            ? vendable.img[0]
                            : displayPlaceholder
                          : displayPlaceholder
                      }
                      className="h-full w-full object-contain"
                    />
                  </div>
                  {vendable && (
                    <div className="font-nunito text-center text-2xl font-medium">
                      {vendable.title}
                    </div>
                  )}
                </div>
                <div className="font-nunito text-center text-lg">
                  <strong>{displayName}</strong> has partnered with{' '}
                  <strong className="text-primary-900">iWarranty</strong> to
                  help you manage all your products and warranties in one place.
                </div>
              </div>
            </div>
          </div>
        )}
        <div
          className={`relative rounded-[6px] bg-white px-8 py-12 md:px-16 md:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)] ${
            currentStep !== 9 ? 'md:h-full' : ''
          }`}
        >
          {loading && <LoadingSpinner overlay={true} size="sm" />}
          {currentStep === 9 && <img src={bgEnd} alt={'lastScreenBg'}></img>}
          {(goBack || title) && BackAndTitle}
          {children}
        </div>
      </div>
    </div>
  );
}

export default AuthContainer;
